import React, { useEffect, useState } from 'react';
import { Box, Paper } from '@mui/material';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import styles from '../../styles/login/rightSection.module.scss';
import { verifySignupToken } from '../../utils/api/signup_apis';
import { STATUS } from '../../utils/api/sharedfunction';
import { useSnackbar } from '../../utils/useSnackbar';
import { setUser } from '../../features/auth/authSlice';
import SignupStep1Email from './components/SignupStep1Email';
import SignupStep2Complete from './components/SignupStep2Complete';
import DEFAULT_ROUTES from '../../constants/routes';

const STEP = {
  EMAIL: 'email',
  VERIFYING: 'verifying',
  COMPLETE_FORM: 'complete_form',
};

export default function SignupRightSection() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [step, setStep] = useState(STEP.EMAIL);
  const [verifiedEmail, setVerifiedEmail] = useState(null);
  const { showSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const token = searchParams.get('token');

  useEffect(() => {
    if (!token || !token.trim()) return;

    setStep(STEP.VERIFYING);
    verifySignupToken({ token })
      .then((res) => {
        if (res.status === STATUS.SUCCESS && res.data?.email) {
          setVerifiedEmail(res.data.email.trim().toLowerCase());
          setStep(STEP.COMPLETE_FORM);
        } else {
          showSnackbar(res.error || 'Verification link expired or invalid.', 'error');
          setStep(STEP.EMAIL);
          setSearchParams({});
        }
      })
      .catch(() => {
        showSnackbar('Verification failed. Please try again.', 'error');
        setStep(STEP.EMAIL);
        setSearchParams({});
      });
  }, [token]);

  const handleEmailSuccess = () => {
    showSnackbar('Check your email for a verification link.', 'success');
  };

  const handleCompleteSuccess = (user) => {
    showSnackbar('Account created successfully.', 'success');
    setSearchParams({});
    setVerifiedEmail(null);
    setStep(STEP.EMAIL);
    if (user) {
      dispatch(setUser({ user }));
      navigate(DEFAULT_ROUTES.BASE_URL);
    } else {
      navigate(DEFAULT_ROUTES.BASE_URL);
    }
  };

  const handleBackToEmail = () => {
    setSearchParams({});
    setVerifiedEmail(null);
    setStep(STEP.EMAIL);
  };

  return (
    <Box className={styles['right-section-container']}>
      <Box className={styles['content-wrapper']}>
        <Box className={styles['branding-section']}>
          <Box className={styles['branding-logo']}>
            <img
              src={require('../../assets/images/APLogo.svg').default}
              alt="QA-Path Logo"
              className={styles['branding-logo-image']}
            />
          </Box>
          <h3 className={styles['branding-title']}>QA-Path</h3>
          <h6 className={styles['branding-subtitle']}>
            Modern AI Enabled Testing Applications
          </h6>
        </Box>
        <Paper elevation={0} className={styles['form-container']}>
          {step === STEP.EMAIL && (
            <>
              <SignupStep1Email onSuccess={handleEmailSuccess} />
              <Box className={styles['footer-section']}>
                <p className={styles['footer-text']}>
                  Already have an account?{' '}
                  <Link
                    to={DEFAULT_ROUTES.LOGIN}
                    className={styles['footer-link']}
                    style={{ textDecoration: 'none' }}
                  >
                    Sign in
                  </Link>
                </p>
              </Box>
            </>
          )}
          {step === STEP.VERIFYING && (
            <Box className={styles['form-header']} py={3}>
              <p className={styles['form-subtitle']}>Verifying your link…</p>
            </Box>
          )}
          {step === STEP.COMPLETE_FORM && verifiedEmail && (
            <SignupStep2Complete
              email={verifiedEmail}
              verificationToken={token}
              onSuccess={handleCompleteSuccess}
              onBack={handleBackToEmail}
            />
          )}
        </Paper>
      </Box>
    </Box>
  );
}
